import React from "react"
import { Box, type BoxProps } from "@mui/material"
import { composeSx } from "../theme/composeSx"
import { SectionHeader } from "../molecules/SectionHeader"

export type ClientDetailPageProps = Omit<BoxProps, "maxWidth"> & {
  /** e.g. "Janice Ng · Treat" */
  eyebrow?: string
  title?: string
  subtitle?: string
  actions?: React.ReactNode
  /**
   * Optional content max width. Default fills the Client Detail main well
   * (`false` / none). Pass a number only for intentionally narrow panes
   * (e.g. coming-soon copy).
   */
  maxWidth?: number | string | false
}

/**
 * Shared Client Detail pane chrome: page header + fluid content column.
 * Sit inside `LoopShell` main (light gray `vira.pageBg` well).
 * Root carries `.client-detail-pane` for descendant token overrides.
 */
export function ClientDetailPage({
  eyebrow,
  title,
  subtitle,
  actions,
  maxWidth = false,
  children,
  className,
  sx,
  ...rest
}: ClientDetailPageProps) {
  const showHeader = Boolean(title)

  return (
    <Box
      className={["client-detail-pane", className].filter(Boolean).join(" ")}
      data-client-detail-pane=""
      sx={composeSx(
        () => ({
          width: "100%",
          maxWidth: maxWidth === false ? "none" : maxWidth,
          minWidth: 0,
          overflowX: "hidden",
        }),
        sx,
      )}
      {...rest}
    >
      {showHeader ? (
        <SectionHeader
          level="page"
          eyebrow={eyebrow}
          title={title!}
          subtitle={subtitle}
          actions={actions}
        />
      ) : null}
      {children}
    </Box>
  )
}
